import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Card,
  Alert,
} from 'reactstrap';

import classnames from 'classnames';
import { connect } from 'react-redux';
import SmartCard from '../../../../shared/components/SmartCard';
import {
  ACTIVE_TAB,
  REPORT_PARAMETERS,
  REPORT_TYPES,
} from '../../../../shared/utils/constants';
import SummaryCharts from './SummaryCharts';
import IndividualAnswer from './IndividualAnswer';
import GroupedQuestions from './GroupedQuestions';
import GraphQl from '../../../../GraphQL';
import {
  getChartIndividual,
  getChartSummary,
} from '../../../../shared/queries/ViewMetrics';
import {
  changeLoadingFalse,
  changeLoadingTrue,
} from '../../../../redux/actions/loadingActions';
import {
  generateReport,
  openReport,
} from '../../../../shared/utils/smartutils';
import MDIconButton from '../../../../shared/components/MDIconButton';

const SurveyMetricsView = (props) => {
  const { t, dispatch, communicationId } = props;
  const [activeTab, setActiveTab] = useState(ACTIVE_TAB.ONE);
  const [summary, setSummary] = useState([]);
  const [individual, setIndividual] = useState([]);

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const toggleTab = (index) => {
    setActiveTab(index);
  };

  const loadSummary = () => {
    showLoading();
    const query = getChartSummary(communicationId);
    GraphQl(query, {})
      .then((res) => {
        setSummary(res.communicationSurveyResultByQuestion);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const loadIndividual = () => {
    showLoading();
    const query = getChartIndividual(communicationId);
    GraphQl(query, {})
      .then((res) => {
        setIndividual(res.communicationSurveyResultInDetail);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const noMetrics = () => {
    return (
      <Alert color="success" className="d-flex justify-content-center">
        {t('notifications.no-survey-metrics')}
      </Alert>
    );
  };

  const printReport = (typeReportValue) => {
    showLoading();
    const formData = new FormData();
    formData.append(REPORT_PARAMETERS.REPORTTYPE, typeReportValue);
    formData.append(REPORT_PARAMETERS.pCO00COMU, communicationId);

    generateReport(formData)
      .then(async (res) => {
        await openReport(res.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  useEffect(() => {
    loadSummary();
    loadIndividual();
  }, []);

  return (
    <SmartCard>
      <Container>
        <div className="tabs tabs--justify tabs--bordered-bottom">
          <div className="tabs__wrap">
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === ACTIVE_TAB.ONE,
                  })}
                  onClick={() => {
                    toggleTab(ACTIVE_TAB.ONE);
                  }}
                >
                  {t('communication.resume')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === ACTIVE_TAB.TWO,
                  })}
                  onClick={() => {
                    toggleTab(ACTIVE_TAB.TWO);
                  }}
                >
                  {t('communication.question')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === ACTIVE_TAB.THREE,
                  })}
                  onClick={() => {
                    toggleTab(ACTIVE_TAB.THREE);
                  }}
                >
                  {t('communication.individualQuestion')}
                </NavLink>
              </NavItem>
            </Nav>
          </div>
          <TabContent activeTab={activeTab}>
            <TabPane tabId={ACTIVE_TAB.ONE}>
              <Container>
                <Row>
                  <Col md={12} lg={12}>
                    <Card>
                      {summary.length ? (
                        <>
                          <div className="card__title d-flex flex-row justify-content-between align-items-baseline">
                            <h5 className="bold-text">
                              {t('communication.surveyDetails.surveyResult')}
                            </h5>
                            <MDIconButton
                              onClick={() =>
                                printReport(REPORT_TYPES.RELA_SURVEY_GROUP)
                              }
                              iconPath="mdiPrinter"
                              size={1.2}
                              tooltip={t('tooltips.download-report')}
                            />
                          </div>
                          <SummaryCharts summary={summary} />
                        </>
                      ) : (
                        <>{noMetrics()}</>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Container>
            </TabPane>
          </TabContent>
          <TabContent activeTab={activeTab}>
            <TabPane tabId={ACTIVE_TAB.TWO}>
              <Container>
                <Row>
                  <Col md={12} lg={12}>
                    <Card>
                      {individual.length ? (
                        <GroupedQuestions
                          individual={individual}
                          printReport={printReport}
                        />
                      ) : (
                        <>{noMetrics()}</>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Container>
            </TabPane>
          </TabContent>
          <TabContent activeTab={activeTab}>
            <TabPane tabId={ACTIVE_TAB.THREE}>
              <Container>
                <Row>
                  <Col md={12} lg={12}>
                    <Card>
                      {individual.length ? (
                        <IndividualAnswer
                          individual={individual}
                          printReport={printReport}
                        />
                      ) : (
                        <>{noMetrics()}</>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Container>
            </TabPane>
          </TabContent>
        </div>
      </Container>
    </SmartCard>
  );
};

SurveyMetricsView.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  communicationId: PropTypes.number.isRequired,
};

export default withTranslation('common')(connect()(SurveyMetricsView));
